import { useEffect, useRef, useState } from "react";
import { getEcho } from "../lib/echo";
import { useAuthStore } from "../store/authStore";
import { useNotificationStore } from "../store/notificationStore";
import type { Notification } from "../types";

export interface GpsPositionPayload {
  vehicle_id: number;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  speed: number;
  heading: number | null;
  ignition_status: boolean | null;
  fuel_level: number | null;
  odometer: number | null;
  recorded_at: string | null;
}

export interface VehicleStatusPayload {
  vehicle: {
    id: number;
    uuid?: string;
    license_plate?: string;
    make?: string;
    model?: string;
  };
  old_status: string | null;
  new_status: string;
}

export interface DispatchUpdatePayload {
  dispatch: Record<string, unknown>;
  event: string;
  message?: string | null;
}

export interface AetrViolationPayload {
  violation: Record<string, unknown>;
}

export interface NotificationCreatedPayload {
  notification: Partial<Notification>;
}

interface RealtimeStatus {
  isConnected: boolean;
  isConnecting: boolean;
  error: Error | null;
}

function useChannel(
  channelName: string,
  bindings: Array<{ event: string; handler: (data: unknown) => void }>,
  enabled = true,
): void {
  const handlerRef = useRef(bindings);
  handlerRef.current = bindings;

  useEffect(() => {
    if (!enabled || !channelName) return;

    const channel = getEcho().channel(channelName);

    const bound = handlerRef.current.map(({ event, handler }) => {
      channel.listen(event, handler as (data: unknown) => void);
      return { event, handler: handler as (data: unknown) => void };
    });

    return () => {
      bound.forEach(({ event, handler }) =>
        channel.stopListening(event, handler),
      );
    };
  }, [channelName, enabled]);
}

export function useGpsPositionUpdates(
  onPosition: (payload: GpsPositionPayload) => void,
  enabled = true,
): void {
  const ref = useRef(onPosition);
  ref.current = onPosition;

  useChannel(
    "gps-updates",
    [
      {
        event: ".position.updated",
        handler: (data) => {
          const payload = data as { position: GpsPositionPayload };
          ref.current(payload.position);
        },
      },
    ],
    enabled,
  );
}

export function useVehicleStatusUpdates(
  onStatusChange: (payload: VehicleStatusPayload) => void,
  enabled = true,
): void {
  const ref = useRef(onStatusChange);
  ref.current = onStatusChange;

  useChannel(
    "fleet-updates",
    [
      {
        event: ".vehicle.status.changed",
        handler: (data) => ref.current(data as VehicleStatusPayload),
      },
    ],
    enabled,
  );
}

export function useDispatchUpdates(
  handlers: {
    onUpdated?: (payload: DispatchUpdatePayload) => void;
    onAetrViolation?: (payload: AetrViolationPayload) => void;
  },
  enabled = true,
): void {
  const ref = useRef(handlers);
  ref.current = handlers;

  useChannel(
    "dispatch-updates",
    [
      {
        event: ".dispatch.updated",
        handler: (data) =>
          ref.current.onUpdated?.(data as DispatchUpdatePayload),
      },
      {
        event: ".aetr.violation.created",
        handler: (data) =>
          ref.current.onAetrViolation?.(data as AetrViolationPayload),
      },
    ],
    enabled,
  );
}

export function useRealtimeNotifications(enabled = true): void {
  const userId = useAuthStore((state) => state.user?.id);

  useChannel(
    `private-notifications.${userId}`,
    [
      {
        event: ".notification.created",
        handler: (data) => {
          const payload = data as NotificationCreatedPayload;
          const notification = payload.notification;
          if (!notification?.id) return;

          useNotificationStore.getState().addNotification({
            id: String(notification.id),
            type: (notification.type as Notification["type"]) || "info",
            title: notification.title || "New notification",
            message: notification.message || "",
            module: notification.module,
            entityId: notification.entityId,
            isRead: Boolean(notification.isRead),
            createdAt: notification.createdAt || new Date().toISOString(),
          });
        },
      },
    ],
    enabled && Boolean(userId),
  );
}

export function useRealtimeConnectionStatus(enabled = true): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>({
    isConnected: false,
    isConnecting: false,
    error: null,
  });

  useEffect(() => {
    if (!enabled) {
      setStatus({ isConnected: false, isConnecting: false, error: null });
      return;
    }

    const echo = getEcho();
    const connector = echo.connector as unknown as {
      pusher?: {
        connection?: {
          state: string;
          bind: (
            event: string,
            callback: (states: { current: string }) => void,
          ) => void;
          unbind: (event: string) => void;
        };
      };
    };
    const pusher = connector.pusher;

    if (pusher?.connection) {
      const update = (state: string) => {
        setStatus({
          isConnected: state === "connected",
          isConnecting: state === "connecting",
          error:
            state === "failed" ? new Error("Realtime connection failed") : null,
        });
      };

      pusher.connection.bind("state_change", (states: { current: string }) =>
        update(states.current),
      );
      update(pusher.connection.state);

      return () => {
        pusher.connection?.unbind("state_change");
      };
    }

    return undefined;
  }, [enabled]);

  return status;
}
